import { useCallback } from 'react';

import useNavigation from '@zego/hooks/useNavigation';
import { useTracking } from '@zego/tracking';

import { urlStateFromApiState } from '../urlState';

export default function CreateUseEstimateForm(nextUrl, useEstimateResult) {
  return function useEstimateForm(
    estimateResultId,
    inputFn,
    eventName,
    estimateType,
  ) {
    const { trackEvent, identifyUser } = useTracking();
    const { call } = useEstimateResult(estimateResultId);
    const navigation = useNavigation();

    return useCallback(
      (rawValues, { onError, onSuccess } = {}) => {
        const { __estimate_form_source, ...values } = rawValues;

        if (eventName && estimateType) {
          trackEvent(eventName, estimateType);
        } else if (eventName) {
          trackEvent(eventName);
        }

        const data = {
          input: inputFn(values),
        };

        if (estimateResultId) {
          data.input.id = estimateResultId;
        }

        call({ variables: data }, (error, data) => {
          if (error) {
            /*
             * We only want to tell the form it's complete if something's gone
             * wrong, otherwise we get into a state where it looks like nothing is
             * happening while the next page is loading.
             */
            if (onError) onError();
            return;
          }

          const state = data.result.estimateResult.state;
          const navigateMethod = estimateResultId
            ? navigation.replace
            : navigation.push;

          const source = navigation.query.source || __estimate_form_source;

          navigateMethod(
            {
              href: `${nextUrl}/[state]/`,
              as: `${nextUrl}/${urlStateFromApiState(state)}/${
                source == null ? '' : `?source=${source}`
              }`,
            },
            {
              query: {
                state: urlStateFromApiState(state),
                source,
              },
              shallow: false,
            },
          );

          const {
            lead: { email, givenNames, lastName, dateOfBirth, phoneNumber },
          } = data.result.estimateResult;

          /* Identify the user with details we have,
          an anonymousId will be generated automatically by Segment tracking */
          identifyUser({
            email,
            firstName: givenNames,
            birthday: new Date(dateOfBirth),
            lastName,
            phone: phoneNumber,
          });

          if (onSuccess) onSuccess();
        });
      },
      [
        call,
        estimateResultId,
        eventName,
        inputFn,
        navigation,
        trackEvent,
        identifyUser,
        estimateType,
      ],
    );
  };
}
